import { Box, SxProps, Theme, Typography } from "@outschool/backpack";
import {
  removeServiceFee,
  serviceFeeCentsOnItem,
  totalSellerPriceCentsForEnrollments,
} from "@outschool/business-rules";
import {
  EnrollmentInput,
  PricingOfferState,
} from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  EnrollmentMeetingPurchaseQueryQuery,
} from "@outschool/gql-frontend-generated";
import { useLocalPriceDisplay, useTranslation } from "@outschool/localization";
import { MarketplaceFeeInfo } from "@outschool/ui-components-website";
import { Space } from "@outschool/ui-legacy-component-library";
import { pxToRem } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import * as Enrollment from "../../../../../shared/Enrollment";
import { useOrderContext } from "../../OrderContext";
import SubscriptionDisclaimer from "../SubscriptionDisclaimer";
import { PriceDisplay, PriceDisplayProps } from "./PriceDisplay";

export type LoadedPriceBreakdownProps = React.PropsWithChildren<{
  // required
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  section?: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"];
  pricingBreakdown: NonNullable<OrderContext["pricingBreakdown"]>;
  promoCode: OrderContext["promoCode"];
  isBuyoutSection: boolean;
  donationInput?: { amountCents: number };
  isWeeklyPayment?: boolean;
  isMeetingPrepay?: boolean;
  priceCents: number;
  canPayLater: boolean;
  // One of the following must be provided
  enrollmentInputs?: EnrollmentInput[];
  learnersAcceptingGiftEnrollments?: NonNullable<
    ActivityEnrollQueryQuery["currentUser"]
  >["children"];
  oneOnOneNextBillingDate?: Date;
  useSimplifiedPaymentUI?: boolean;
}>;

type OrderContext = ReturnType<typeof useOrderContext>;

const VALID_OFFER_BOX_SX: SxProps = {
  marginBottom: "1em",
  background: "rgba(43, 167, 206, 0.05)",
  border: "1px solid rgba(43, 167, 206, 0.2)",
  borderRadius: "0.25em",
  padding: "1em",
};

const ROW_SX: SxProps = {
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: "0.25em",
};

export function LoadedPriceBreakdown({
  activity,
  section,
  pricingBreakdown,
  promoCode,
  enrollmentInputs,
  learnersAcceptingGiftEnrollments,
  donationInput,
  isBuyoutSection,
  isWeeklyPayment,
  isMeetingPrepay,
  priceCents,
  oneOnOneNextBillingDate,
  canPayLater = false,
  useSimplifiedPaymentUI,
  children,
}: LoadedPriceBreakdownProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\LoadedPriceBreakdown",
  );

  const { renderCurrencyString, isBaseCurrency, currencyDisclaimer } =
    useLocalPriceDisplay();

  const { pricingOffer, pricingOfferState, serviceFeePercentage } =
    pricingBreakdown ?? {};

  const [giftEnrollmentInputs, nonGiftEnrollmentInputs]: [
    EnrollmentInput[],
    EnrollmentInput[],
  ] = _.partition(enrollmentInputs, e => e.isGift);

  const subtotal = pricingBreakdown?.originalTotalCents;
  const dueTotal = canPayLater ? 0 : pricingBreakdown?.stripeCents ?? 0;
  const isBuyerOrgPricingOffer = Boolean(pricingOffer?.buyerOrgName);

  const notEligiblePricingOfferCopy = isBuyerOrgPricingOffer
    ? t("This purchase is not eligible for your {{organizationName}} offer.", {
        organizationName: pricingOffer?.buyerOrgName,
      })
    : t("This purchase is not eligible for our financial assistance offer.");
  const isOngoing = !!activity.is_ongoing_weekly;
  const isOneOnOne = !!activity.hasTeacherSchedule;
  const hasProratedCurrentWeek =
    isWeeklyPayment &&
    pricingBreakdown?.hasProratedEnrollments &&
    pricingBreakdown?.perEnrollmentCurrentWeekCents !==
      pricingBreakdown?.perEnrollmentWeeklyCents;
  const hasProratedTotal =
    hasProratedCurrentWeek ||
    (!isWeeklyPayment && pricingBreakdown?.hasProratedEnrollments);

  const periodText =
    isOneOnOne && isMeetingPrepay
      ? t("per session")
      : isOngoing || isWeeklyPayment
      ? t("per week")
      : activity.isClub
      ? t("per month")
      : "";

  const subtotalPeriodText = hasProratedCurrentWeek
    ? t("prorated")
    : periodText;

  const weeklySubscriptionRenewalPriceText = renderCurrencyString({
    priceInCents: totalSellerPriceCentsForEnrollments(enrollmentInputs!),
  });
  const spendingLimit = renderCurrencyString({
    priceInCents: pricingOffer?.userLimitCents ?? 0,
  });
  const promoCodeUpper = promoCode?.toUpperCase();
  const totalText =
    isOngoing || activity.isClub ? t`Total` : t`Total due today`;

  // Since we are getting the enrollment price from the pricing breakdown
  // for weekly payments and one-on-ones, the service fee has already
  // been applied, so we remove it to calculate the price before the
  // service fee line item is added
  const shouldCalculatePriceWithoutServiceFee =
    !!serviceFeePercentage && (isWeeklyPayment || isOneOnOne);

  return (
    <Box
      sx={{
        position: "relative",
      }}
      aria-live="polite"
    >
      {pricingOfferState === PricingOfferState.ValidFinancialAid && (
        <Box sx={VALID_OFFER_BOX_SX}>
          <Box>
            {t`This class is discounted because you registered for financial assistance.`}
          </Box>
          <Space y="medium" />
          <Box
            sx={{
              fontWeight: "fontWeightBold",
            }}
          >
            {t`Please use this donation responsibly so as many families can benefit from it as possible.`}
          </Box>
          {(enrollmentInputs?.length ?? 0) > 0 && isOngoing && (
            <Box
              sx={{
                marginTop: "1em",
              }}
            >
              {t(
                "Your weekly subscription renewal will cost {{weeklySubscriptionRenewalPriceText}} \
when this offer ends. We will email you at least 24 hours in advance before we start charging your card.",
                { weeklySubscriptionRenewalPriceText },
              )}
            </Box>
          )}
        </Box>
      )}

      {pricingOfferState === PricingOfferState.InvalidDate && (
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          {`${notEligiblePricingOfferCopy} ${t`It is out of the date range for the offer.`}`}
        </Box>
      )}

      {pricingOfferState === PricingOfferState.InvalidOverTotalCap && (
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          {`${notEligiblePricingOfferCopy} ${t`The program has run out of budgeted \
funds for Outschool classes. All classes are now full price.`}`}
        </Box>
      )}

      {pricingOfferState === PricingOfferState.InvalidOverUserCap && (
        <Box
          sx={{
            marginBottom: "1em",
          }}
        >
          {isBuyerOrgPricingOffer
            ? t(
                'You have reached the spending limit of {{spendingLimit}} for your "{{organizationName}}" \
pricing offer. All classes are now full price.',
                { spendingLimit, organizationName: pricingOffer?.buyerOrgName },
              )
            : t(
                "You have reached the spending limit of {{spendingLimit}} for your financial assistance offer. \
All classes are now full price.",
                { spendingLimit },
              )}
        </Box>
      )}

      {!!section?.allowsLateEnrollment && !!section?.progress && (
        <Box
          sx={(theme: Theme) => ({
            border: "1px solid",
            borderColor: theme.palette.neutral.light,
            borderRadius: theme.spacing(8),
            padding: theme.spacing(16),
            marginBottom: theme.spacing(20),
          })}
        >
          <Typography variant="body1" emphasized>
            {t(
              "{{pastMeetingCount}} of {{totalMeetingCount}} meetings has already occurred. ",
              {
                pastMeetingCount: section.progress.pastMeetingCount,
                totalMeetingCount: section.progress.totalMeetingCount,
                count: section.progress.pastMeetingCount,
                defaultValue_plural:
                  "{{pastMeetingCount}} of {{totalMeetingCount}} meetings have already occurred. ",
              },
            )}
          </Typography>
          <Typography variant="body1">
            {t(
              "Pay only for the remaining {{remainingMeetingCount}} meeting.",
              {
                remainingMeetingCount: section.progress.remainingMeetingCount,
                count: section.progress.remainingMeetingCount,
                defaultValue_plural:
                  "Pay only for the remaining {{remainingMeetingCount}} meetings.",
              },
            )}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: "#F0F3FF",
          borderRadius: 16,
          padding: "0.5em",
        }}
      >
        <Box sx={{ padding: "0.5em" }}>
          {nonGiftEnrollmentInputs.map((enrollmentInput, index) => (
            <PriceBreakdownEnrollment
              key={enrollmentInput.learner?.uid || index}
              enrollmentInput={enrollmentInput}
              periodText={periodText}
              pricingBreakdown={pricingBreakdown}
              isWeeklyPayment={isWeeklyPayment}
              durationWeeks={activity.duration_weeks!}
              serviceFeePercentage={serviceFeePercentage}
            />
          ))}
          {giftEnrollmentInputs && (
            <PriceBreakdownGiftEnrollments
              enrollmentInputs={giftEnrollmentInputs}
              enrollmentDiscountPercentage={
                activity.pricingInfo?.upfrontDiscountPercentage ?? 0
              }
              enrollmentWeeklyTotalPrice={
                activity.pricingInfo?.totalPriceCentsForWeeklyPayments ?? 0
              }
              shouldCalculatePriceWithoutServiceFee={
                shouldCalculatePriceWithoutServiceFee
              }
              serviceFeePercentage={serviceFeePercentage}
            />
          )}
          {Boolean(donationInput) && (
            <PriceBreakdownDonation amountCents={donationInput?.amountCents} />
          )}
          {learnersAcceptingGiftEnrollments?.map(
            (learner: $TSFixMe, index: $TSFixMe) => (
              <PriceBreakdownLearnerAcceptingGift
                key={learner.uid || index}
                learner={learner}
              />
            ),
          )}

          {!isBuyoutSection &&
            enrollmentInputs &&
            pricingBreakdown &&
            !!pricingBreakdown.perEnrollmentUpfrontCents &&
            !!pricingBreakdown.perEnrollmentTotalWeeklyCents &&
            !isWeeklyPayment &&
            activity.pricingInfo &&
            activity.pricingInfo.upfrontDiscountPercentage > 0 && (
              <Box
                sx={{
                  color: "success.600",
                }}
                data-test-id="upfront-discount"
              >
                <Box
                  flex
                  sx={ROW_SX}
                  data-test-id="upfront-discount-adjustment"
                >
                  <PriceAdjustment
                    label={t("Upfront payment discount ({{percentageOff}}%)", {
                      percentageOff:
                        activity.pricingInfo.upfrontDiscountPercentage,
                    })}
                    adjustmentCents={removeServiceFee(
                      pricingBreakdown.perEnrollmentUpfrontCents *
                        enrollmentInputs.length -
                        pricingBreakdown.perEnrollmentTotalWeeklyCents *
                          enrollmentInputs.length,
                      pricingBreakdown.serviceFeePercentage,
                    )}
                  />
                </Box>
              </Box>
            )}
          {!!serviceFeePercentage && serviceFeePercentage > 0 && (
            <ServiceFeeLineItem
              pricingBreakdown={pricingBreakdown}
              serviceFeePercentage={serviceFeePercentage}
              periodText={periodText}
            />
          )}

          <Box
            sx={{
              height: 0,
              margin: pxToRem(12, 0),
              borderTop: "1px solid",
              borderColor: "grey.300",
            }}
          ></Box>
          {!!subtotal && subtotal !== dueTotal && !canPayLater && (
            <>
              <Box flex sx={ROW_SX}>
                <Box>{t`Subtotal` + ":"}</Box>
                <PriceDisplay
                  amountCents={subtotal}
                  periodText={subtotalPeriodText}
                />
              </Box>
              <Box
                sx={{
                  color: "#29a877",
                }}
                data-test-id="discount"
              >
                {isBuyoutSection && (
                  <Box flex sx={ROW_SX} data-test-id="buyout-adjustment">
                    <PriceAdjustment
                      label={t`Teacher price adjustment:`}
                      adjustmentCents={
                        pricingBreakdown.totalCents -
                        pricingBreakdown.originalTotalCents
                      }
                    />
                  </Box>
                )}
                {Boolean(pricingBreakdown.pricingOfferCents) && (
                  <>
                    <Box
                      flex
                      sx={ROW_SX}
                      data-test-id="pricing-offer-adjustment"
                    >
                      <PriceAdjustment
                        label={t("{{organizationName}} sponsorship:", {
                          organizationName: pricingOffer?.buyerOrgName,
                        })}
                        adjustmentCents={-pricingBreakdown.pricingOfferCents}
                      />
                    </Box>
                    <Box flex sx={ROW_SX}>
                      <ExtraLineItem
                        label={t(
                          "Current {{organizationName}} Wallet Balance",
                          {
                            organizationName: pricingOffer?.buyerOrgName,
                          },
                        )}
                        amountCents={pricingOffer?.remainingCapCents ?? 0}
                        dataTestId="wallet-before-balance"
                      />
                    </Box>
                  </>
                )}
                {Boolean(pricingBreakdown.storedCreditCents) && (
                  <Box flex sx={ROW_SX} data-test-id="credit-adjustment">
                    <PriceAdjustment
                      label={t("Outschool Balance") + ":"}
                      adjustmentCents={-pricingBreakdown.storedCreditCents}
                    />
                  </Box>
                )}
                {Boolean(pricingBreakdown.couponCents) && (
                  <Box
                    flex
                    sx={[
                      {
                        color: "success.600",
                      },
                      ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
                    ]}
                    data-test-id="coupon-adjustment"
                  >
                    <PriceAdjustment
                      label={t('"{{promoCode}}" promo:', {
                        promoCode: promoCodeUpper,
                      })}
                      adjustmentCents={-pricingBreakdown.couponCents}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}

          <Box
            flex
            sx={[
              {
                fontWeight: 700,
              },
              ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
            ]}
          >
            <Box>{totalText}:</Box>
            <PriceDisplay
              amountCents={!!enrollmentInputs?.length ? dueTotal : "-"}
              dataTestId="total-price"
              showWarning={!isBaseCurrency && Boolean(currencyDisclaimer)}
              useFree={!canPayLater}
              periodText={hasProratedTotal ? t("prorated") : ""}
              periodTextSx={
                hasProratedTotal ? { fontWeight: "bold" } : undefined
              }
            />
          </Box>
          {activity.isClub && priceCents > 0 && (
            <Typography
              variant="inherit"
              sx={{
                fontSize: 14,
              }}
            >
              {t`Prorated for your first month`}
            </Typography>
          )}

          {Boolean(pricingOffer) && pricingBreakdown.pricingOfferCents != 0 && (
            <Box flex sx={ROW_SX}>
              <ExtraLineItem
                label={t("Remaining {{organizationName}} Wallet Balance", {
                  organizationName: pricingOffer?.buyerOrgName,
                })}
                amountCents={
                  (pricingOffer?.remainingCapCents ?? 0) -
                  (pricingBreakdown?.pricingOfferCents ?? 0)
                }
                dataTestId="wallet-after-balance"
              />
            </Box>
          )}
        </Box>
        {!useSimplifiedPaymentUI && priceCents > 0 && (
          <SubscriptionDisclaimer
            activity={activity}
            isWeeklyPayment={!!isWeeklyPayment}
            oneOnOneNextBillingDate={oneOnOneNextBillingDate}
            canPayLater={canPayLater}
            isMeetingPrepay={isMeetingPrepay}
            isSubscriptionCreditPurchase={false}
          />
        )}
        {children}
      </Box>
    </Box>
  );
}

function PriceBreakdownDonation({ amountCents }: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\LoadedPriceBreakdown",
  );
  const donationText = t("Donation");
  if (amountCents && amountCents > 0) {
    return (
      <Box
        flex
        sx={[
          {
            color: "grey.500",
          },
          ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
        ]}
      >
        <Box>{donationText}</Box>
        <PriceDisplay amountCents={amountCents} />
      </Box>
    );
  }
  return null;
}

type PriceBreakdownEnrollmentProps = {
  enrollmentInput: EnrollmentInput;
  periodText: string;
  pricingBreakdown: NonNullable<OrderContext["pricingBreakdown"]>;
  isWeeklyPayment?: boolean;
  durationWeeks?: number;
  serviceFeePercentage?: number | null;
};

export function PriceBreakdownEnrollment({
  enrollmentInput,
  periodText,
  pricingBreakdown,
  isWeeklyPayment,
  durationWeeks,
  serviceFeePercentage,
}: PriceBreakdownEnrollmentProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\LoadedPriceBreakdown",
  );

  const name =
    Enrollment.displayName(enrollmentInput, enrollmentInput.learner) || "-----";
  const age = Enrollment.displayAge(enrollmentInput, enrollmentInput.learner);
  const displayAge = age ? t("age {{age}}", { age }) : "--";

  let amountCents: number | null = null;
  if (pricingBreakdown.perEnrollmentWeeklyCents) {
    if (isWeeklyPayment) {
      amountCents = pricingBreakdown.perEnrollmentWeeklyCents;
    } else if (pricingBreakdown.perEnrollmentTotalWeeklyCents) {
      // if we have a prorated enrollment which has a weekly surcharge,
      // we should show the non-prorated rate with the weekly surcharge
      amountCents = pricingBreakdown?.hasProratedEnrollments
        ? removeServiceFee(
            pricingBreakdown.perEnrollmentWeeklyCents,
            serviceFeePercentage,
          ) * (durationWeeks ?? 0)
        : pricingBreakdown.perEnrollmentTotalWeeklyCents;
    } else {
      amountCents = pricingBreakdown.perEnrollmentUpfrontCents;
    }
  }

  if (amountCents === null) {
    amountCents =
      isWeeklyPayment && durationWeeks
        ? Math.round(
            (pricingBreakdown.perEnrollmentUpfrontCents ??
              enrollmentInput.price_cents) / durationWeeks,
          )
        : pricingBreakdown.perEnrollmentUpfrontCents ??
          enrollmentInput.price_cents;
  }

  const upfrontProratedAmountCents =
    !pricingBreakdown?.hasProratedEnrollments || !!isWeeklyPayment
      ? 0
      : serviceFeePercentage && pricingBreakdown?.perEnrollmentTotalWeeklyCents
      ? removeServiceFee(
          pricingBreakdown.perEnrollmentTotalWeeklyCents,
          serviceFeePercentage,
        )
      : pricingBreakdown?.perEnrollmentUpfrontCents;

  return (
    <Box
      flex
      sx={[
        {
          color: "grey.900",
        },
        ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
      ]}
    >
      <Box className="amp-block" data-private>
        {name} ({displayAge})
      </Box>
      <Box flex sx={{ flexDirection: "row" }}>
        {!!upfrontProratedAmountCents ? (
          <PriceDisplay
            amountCents={amountCents}
            proratedAmountCents={upfrontProratedAmountCents}
            periodText={periodText}
          />
        ) : (
          <PriceDisplay
            amountCents={removeServiceFee(amountCents, serviceFeePercentage)}
            periodText={periodText}
          />
        )}
      </Box>
    </Box>
  );
}

function ServiceFeeLineItem({
  pricingBreakdown,
  serviceFeePercentage,
  periodText,
}: {
  pricingBreakdown: OrderContext["pricingBreakdown"];
  serviceFeePercentage: number;
  periodText: string;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\LoadedPriceBreakdown",
  );

  const amountCents = pricingBreakdown?.totalCents ?? 0;
  const serviceFeeCents = serviceFeeCentsOnItem(
    amountCents,
    serviceFeePercentage,
  );

  return (
    <Box
      flex
      sx={[
        {
          color: "grey.600",
        },
        ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
      ]}
    >
      <Typography sx={{ color: "grey.600" }}>
        {t("Marketplace fee")}
        <MarketplaceFeeInfo />
      </Typography>
      <PriceDisplay amountCents={serviceFeeCents} periodText={periodText} />
    </Box>
  );
}

type PriceBreakdownGiftEnrollmentsProps = {
  enrollmentInputs: EnrollmentInput[];
  enrollmentDiscountPercentage: number;
  enrollmentWeeklyTotalPrice: number;
  shouldCalculatePriceWithoutServiceFee?: boolean;
  serviceFeePercentage?: number | null;
};

function PriceBreakdownGiftEnrollments({
  enrollmentInputs,
  enrollmentDiscountPercentage,
  enrollmentWeeklyTotalPrice,
  shouldCalculatePriceWithoutServiceFee,
  serviceFeePercentage,
}: PriceBreakdownGiftEnrollmentsProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\LoadedPriceBreakdown",
  );
  const totalNumber = enrollmentInputs.length;

  if (totalNumber > 0) {
    const originalPrice =
      enrollmentDiscountPercentage > 0 && enrollmentWeeklyTotalPrice
        ? enrollmentWeeklyTotalPrice * totalNumber
        : totalSellerPriceCentsForEnrollments(enrollmentInputs);

    const serviceFeeCents =
      serviceFeePercentage && shouldCalculatePriceWithoutServiceFee
        ? serviceFeeCentsOnItem(originalPrice, serviceFeePercentage)
        : 0;

    return (
      <Box
        flex
        sx={[
          {
            color: "grey.500",
          },
          ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
        ]}
      >
        <Box>
          {t("{{totalNumber}} Gift enrollment", {
            count: totalNumber,
            totalNumber,
            defaultValue_plural: "{{totalNumber}} Gift enrollments",
          })}
        </Box>
        <PriceDisplay amountCents={originalPrice - serviceFeeCents} />
      </Box>
    );
  }
  return null;
}

type PriceBreakdownLearnerAcceptingGiftProps = {
  learner: { name?: string; age?: number };
};

function PriceBreakdownLearnerAcceptingGift({
  learner,
}: PriceBreakdownLearnerAcceptingGiftProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceBreakdown\\LoadedPriceBreakdown",
  );
  const { name, age } = learner;
  return (
    <Box
      flex
      sx={[
        {
          color: "grey.500",
        },
        ...(Array.isArray(ROW_SX) ? ROW_SX : [ROW_SX]),
      ]}
    >
      <Box className="amp-block" data-private>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {name?.trim() ?? "-----"} ({t("age {{age}}", { age })})
      </Box>
      <PriceDisplay amountCents={0} />
    </Box>
  );
}

type PriceAdjustmentProps = {
  label: string;
} & (
  | { adjustmentCents: number; adjustmentCredits?: never }
  | { adjustmentCents?: never; adjustmentCredits: number }
);

// Either adjustmentCents or adjustmentCredits must be provided, but not both or none.
export function PriceAdjustment({
  label,
  adjustmentCents,
  adjustmentCredits,
}: PriceAdjustmentProps) {
  const isDiscount =
    adjustmentCents !== undefined
      ? adjustmentCents < 0
      : adjustmentCredits! < 0;
  const dataTestId = `adjustment-amount-${_.kebabCase(label)}`;
  return (
    <>
      <Box>{label}</Box>
      <PriceDisplay
        {...(adjustmentCents !== undefined
          ? { amountCents: adjustmentCents }
          : { amountCredits: adjustmentCredits! })}
        dataTestId={dataTestId}
        showPlus={!isDiscount}
      />
    </>
  );
}

type ExtraLineItemProps = Omit<PriceDisplayProps, "useFree"> & {
  label: string;
};

/**
 * A non-price line item (like wallet balance)
 */
function ExtraLineItem(props: ExtraLineItemProps) {
  const priceDisplayProps = { ...props, useFree: false };

  return (
    <>
      <Box>{props.label}</Box>
      <PriceDisplay {...(priceDisplayProps as PriceDisplayProps)} />
    </>
  );
}
